import { Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, debounceTime, filter, Observable, Subject, takeUntil } from 'rxjs';
import { LocalizationService } from '../../services/localization.service';
import { DropdownInputsSignalService } from 'src/app/core/services/dropdown-inputs-signal.service';

@Component({
  selector: 'app-select-input-row',
  templateUrl: './select-input-row.component.html',
  styleUrl: './select-input-row.component.scss',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SelectInputRowComponent),
    multi: true
  }]
})
export class SelectInputRowComponent implements OnDestroy, OnInit, OnChanges {
  @Input({ required: true }) bindLabel!: string | { [lang: string]: string };
  @Input({ required: true }) bindValue!: string;
  @Input({ required: true }) placeholder!: string;
  @Input({ required: false }) label?: string;
  @Input({ required: true }) errorMessage!: string;

  @Input({ required: false }) control?: FormControl;
  @Input({ required: true }) fetchFn!: (pageNumber: number, pageSize: number, term: string)
    => Observable<any>;
  @Input({ required: true }) fetchSelectedItem!: (id: any) => Observable<any>;

  @Input({ required: false }) width = '30%';
  @Input({ required: false }) id?: string;
  @Input({ required: false }) writeValueFlag = true;

  @Input() appendTo = '';

  @Input() refresh = 0;
  @Output() emitSelectOption = new EventEmitter<any>();

  private destroy$ = new Subject<void>();
  private searchInput$ = new Subject<string>();

  currentBindLabel: string;

  value: any;
  onChange = (value: any) => { };
  onTouched = () => { };
  disabled = false;

  items$ = new BehaviorSubject<any[]>([]);
  isLoading = false;

  currentPage = 1;
  pageSize = 10;
  searchPageSize = 100;
  hasMoreItems = true;
  private lastSearchTerm = '';

  /** تعطيل فلترة ng-select المحلية — البحث يتم عبر الـ API فقط */
  readonly disableLocalFilter = (): boolean => true;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.refresh && changes.refresh.currentValue !== 0) {
      this.onValueChange(null);
      this.loadItems('', 1);
    }
  }

  constructor(
    private localaztionService: LocalizationService,
    private dropdownSignalService: DropdownInputsSignalService
  ) { }

  private unwrapData<T>(res: unknown): T | null {
    if (typeof res !== 'object' || res === null) {
      return null;
    }

    const maybe = res as { data?: unknown };
    if (typeof maybe.data !== 'undefined') {
      return maybe.data as T;
    }

    return res as T;
  }

  private unwrapList(res: unknown): unknown[] {
    if (Array.isArray(res)) {
      return res;
    }

    if (typeof res === 'object' && res !== null) {
      const maybe = res as { data?: unknown };
      if (Array.isArray(maybe.data)) {
        return maybe.data;
      }
    }

    return [];
  }

  getLabel = (): void => {
    if (typeof this.bindLabel === 'string') {
      this.currentBindLabel = this.bindLabel;
      return;
    }

    this.currentBindLabel = this.localaztionService.getCachedLanguage() === 'ar'
      ? this.bindLabel['ar']
      : this.bindLabel['en'];
  };

  ngOnInit(): void {
    this.searchInput$
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((term) => this.loadItems(term, 1));

    this.loadItems('', 1);
    this.getLabel();

    this.localaztionService.currentLanguage$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.getLabel();
      });

    this.dropdownSignalService.reload$
      .pipe(
        takeUntil(this.destroy$),
        filter(reloadId => reloadId === this.id)
      )
      .subscribe(() => {
        this.loadItems(this.lastSearchTerm, 1);
      });

    if (this.control?.value) {
      this.writeValue(this.control.value);
    }
  }

  onSearch(term: string): void {
    this.searchInput$.next((term ?? '').trim());
  }

  onClearSearch(): void {
    this.searchInput$.next('');
  }

  private loadItems(term: string, pageNumber: number): void {
    this.lastSearchTerm = term;
    this.currentPage = pageNumber;
    const pageSize = term ? this.searchPageSize : this.pageSize;
    this.isLoading = true;

    this.fetchFn(pageNumber, pageSize, term)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.isLoading = false;
          const data = this.unwrapList(res);

          if (pageNumber === 1) {
            this.items$.next(data);
          } else {
            this.items$.next([...this.items$.getValue(), ...data]);
          }

          this.hasMoreItems = data.length >= pageSize;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  onLoadMore(): void {
    if (!this.hasMoreItems || this.isLoading) {
      return;
    }

    this.loadItems(this.lastSearchTerm, this.currentPage + 1);
  }

  writeValue(value: any): void {
    this.value = value;

    if (value == null) {
      return;
    }

    const currentItems = this.items$.getValue();
    const found = currentItems.find(item => item[this.bindValue] === value);

    if (!found && this.fetchSelectedItem) {
      this.isLoading = true;

      this.fetchSelectedItem(value)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            this.isLoading = false;
            const item = this.unwrapData<Record<string, unknown>>(res);
            if (item) {
              this.items$.next([...currentItems, item]);
            }
          },
          error: (error) => {
            this.isLoading = false;
            console.error('Error fetching selected item:', error);
          },
        });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onValueChange(value: any): void {
    this.value = value;
    this.onChange(value);
    this.control?.setValue(value);

    const selectedItem = this.items$.getValue().find(item => item[this.bindValue] === value);
    this.emitSelectOption.emit(selectedItem);
  }

  isInvalid(): boolean {
    const control = this.control;
    return control ? control.invalid && (control.dirty || control.touched) : false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchInput$.complete();
  }
}
